import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import * as XLSX from 'xlsx';
import { 
  Search, Plus, Trash2, Download, Edit2, Save, X, 
  ChevronLeft, ChevronRight, Clipboard, Lock, Settings, Database,
  Monitor, Laptop, Globe, AlertTriangle, Tablet,
  Navigation, Ruler, History, Clock, Map as MapIcon, ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import ModalConfirm from '../components/ModalConfirm';
import MapaEscola from '../components/MapaEscola';

const Dashboard = ({ showToast, tema }) => {
  // Captura dinâmica do CIE diretamente do final do path da URL (Ex: /escola/18144)
  const pathParts = window.location.pathname.split('/');
  const cieDaUrl = pathParts[pathParts.length - 1];

  const [escolas, setEscolas] = useState([]);
  const [busca, setBusca] = useState('');
  const [escolaSel, setEscolaSel] = useState(null);
  const [inventario, setInventario] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [confirmacao, setConfirmacao] = useState({ aberto: false, id: null, massa: false });
  const [metricasRota, setMetricasRota] = useState(null);
  const [showMap, setShowMap] = useState(false);
  
  const [editandoId, setEditandoId] = useState(null);
  const [tempEditData, setTempEditData] = useState({});
  const [selecionados, setSelecionados] = useState([]);
  const [filtroInterno, setFiltroInterno] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  // Estado do formulário atualizado conforme seu SELECT do banco
  const [formData, setFormData] = useState({ 
    tipo: 'Desktop', 
    mac_address: '', 
    patrimonio: '', 
    descricao: '', 
    obs: '', 
    em_uso: 1,
    status: 'Operacional'
  });

  // Isolamos a função para permitir que o useEffect de inicialização também consiga chamá-la
  const selecionarEscola = (cie) => {
    setMetricasRota(null);
    setShowMap(false);
    api.get(`/escola/${cie}`).then(res => {
      if (res.data) {
        setEscolaSel(res.data);
        setBusca('');
        carregarInventario(cie);
      }
    }).catch(() => showToast("Erro ao carregar dados", "error"));
  };

  // Carrega a lista inicial de escolas e verifica se deve autofiltrar pelo parâmetro da URL
  useEffect(() => {
    api.get('/escolas')
      .then(res => {
        const listaEscolas = Array.isArray(res.data) ? res.data : [];
        setEscolas(listaEscolas);

        // Se houver um CIE válido numérico na URL, filtra a escola automaticamente
        if (cieDaUrl && !isNaN(cieDaUrl)) {
          selecionarEscola(cieDaUrl);
        }
      })
      .catch(console.error);
  }, [cieDaUrl]);

  const carregarInventario = (cie) => {
    api.get(`/inventario/${cie}`)
      .then(res => setInventario(Array.isArray(res.data) ? res.data : []))
      .catch(() => setInventario([]));
    setSelecionados([]);
    setPaginaAtual(1);
  };

  const executarExclusao = async () => {
    try {
      if (confirmacao.massa) {
        await api.post('/inventario/delete-massa', { ids: selecionados });
        showToast("Itens removidos!", 'success');
      } else {
        await api.delete(`/inventario/${confirmacao.id}`);
        showToast("Item excluído!", 'success');
      }
      carregarInventario(escolaSel.dados.cie);
    } catch (err) {
      showToast("Erro ao excluir", 'error');
    } finally {
      setConfirmacao({ aberto: false, id: null, massa: false });
    }
  };

  const iniciarEdicao = (item) => {
    setEditandoId(item.id);
    setTempEditData({ ...item });
  };

  const cancelarEdicao = () => {
    setEditandoId(null);
    setTempEditData({});
  };

  const salvarEdicao = async () => {
    try {
      await api.put(`/inventario/${editandoId}`, tempEditData);
      setEditandoId(null);
      showToast("Alterações salvas!", 'success');
      carregarInventario(escolaSel.dados.cie);
    } catch (err) { showToast("Erro ao salvar", 'error'); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copiado!`, 'success');
  };

  const exportarExcel = () => {
    if (!inventario || inventario.length === 0) return showToast("Sem dados!", 'error');
    const ws = XLSX.utils.json_to_sheet(inventario);
    const wb = XLSX.utils.book_new();
    XXLSX.utils.book_append_sheet(wb, ws, "Inventario");
    XXLSX.writeFile(wb, `Inventario_${escolaSel.dados.cie}.xlsx`);
  };

  const salvarEquipamento = async (e) => {
    e.preventDefault();
    try {
      await api.post('/inventario', { ...formData, escola_cie: escolaSel.dados.cie });
      setModalAberto(false);
      setFormData({ tipo: 'Desktop', mac_address: '', patrimonio: '', descricao: '', obs: '', em_uso: 1, status: 'Operacional' });
      showToast("Ativo cadastrado!", 'success');
      carregarInventario(escolaSel.dados.cie);
    } catch (err) { showToast("Erro ao cadastrar", 'error'); }
  };

  const inventarioFiltrado = useMemo(() => {
    return inventario.filter(i => 
      (i.patrimonio || '').includes(filtroInterno) || 
      (i.mac_address || '').toLowerCase().includes(filtroInterno.toLowerCase())
    );
  }, [inventario, filtroInterno]);

  const totalPaginas = Math.ceil(inventarioFiltrado.length / itensPorPagina) || 1;
  const inventarioPaginado = inventarioFiltrado.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500 pb-10 text-slate-900 dark:text-white">
      <ModalConfirm 
        aberto={confirmacao.aberto} 
        titulo="Excluir Registro" 
        mensagem={confirmacao.massa ? `Deseja apagar os ${selecionados.length} itens?` : "Tem certeza que deseja remover este item?"} 
        aoConfirmar={executarExclusao} 
        aoCancelar={() => setConfirmacao({ aberto: false, id: null, massa: false })} 
      />

      {/* BUSCA DE UNIDADES */}
      <div className="relative mt-2">
        <div className="relative group">
          <input 
            type="text" 
            className="w-full bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 rounded-3xl py-6 pl-16 focus:border-emerald-500 outline-none text-lg font-bold transition-all shadow-sm" 
            placeholder="Digite nome ou CIE da unidade..." 
            value={busca} 
            onChange={(e) => setBusca(e.target.value)} 
          />
          <Search size={28} className="absolute left-6 top-6 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
        </div>
        {busca.length > 0 && (
          <div className="absolute w-full mt-2 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl z-[100] max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {escolas.filter(e => e.nome_escola?.toLowerCase().includes(busca.toLowerCase()) || String(e.cie).includes(busca)).map(e => (
              <button key={e.cie} onClick={() => selecionarEscola(e.cie)} className="w-full text-left px-8 py-4 hover:bg-emerald-50 dark:hover:bg-emerald-600/20 transition-all group">
                <p className="font-black text-emerald-600 dark:text-emerald-400">{e.nome_escola}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">CIE: {String(e.cie).padStart(6, '0')}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {!escolaSel ? (
        <div className="flex flex-col items-center justify-center py-40 opacity-20 grayscale">
          <Search size={100} />
          <p className="mt-6 text-2xl font-black text-center">Selecione uma Unidade</p>
        </div>
      ) : (
        <>
          {/* CABEÇALHO DA ESCOLA */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] border-2 border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 shadow-sm">
            <div className="text-center md:text-left">
              <h2 className="text-2xl lg:text-3xl font-black">{escolaSel.dados.nome_escola}</h2>
              <p className="text-slate-400 dark:text-slate-500 text-xs font-bold tracking-widest uppercase">
                CIE: {escolaSel.dados.cie} • {escolaSel.dados.endereco}
              </p>
            </div>
            
            {/* BOTÕES COMENTADOS ABAIXO */}
            {/* <div className="flex gap-4 w-full md:w-auto">
              {selecionados.length > 0 && (
                <button onClick={() => setConfirmacao({ aberto: true, massa: true })} className="flex-1 bg-red-600 text-white px-6 py-4 rounded-2xl font-black text-xs flex items-center gap-2 transition-transform active:scale-95 shadow-lg shadow-red-500/20">
                  <Trash2 size={18}/> ({selecionados.length})
                </button>
              )}
              <button onClick={exportarExcel} className="flex-1 bg-slate-100 dark:bg-white text-slate-900 dark:text-black px-6 py-4 rounded-2xl font-black text-xs flex items-center gap-2 transition-transform active:scale-95 border border-slate-200 dark:border-transparent">
                <Download size={18}/> Excel
              </button>
              <button onClick={() => setModalAberto(true)} className="flex-1 bg-emerald-600 text-white px-6 py-4 rounded-2xl font-black text-xs flex items-center gap-2 transition-transform active:scale-95 shadow-lg shadow-emerald-500/20">
                <Plus size={18}/> Ativo
              </button>
            </div> 
            */}
          </div>
      
          {/* ACORDIÃO DO MAPA */}
          <div className="bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 rounded-[32px] overflow-hidden shadow-sm">
            <button onClick={() => setShowMap(!showMap)} className="w-full flex items-center justify-between p-6 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all group">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-xl transition-colors ${showMap ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:text-emerald-500'}`}>
                  <MapIcon size={20} />
                </div>
                <div className="text-left">
                  <h3 className="font-black text-sm uppercase tracking-widest">Localização e Navegação</h3>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase">Clique para {showMap ? 'ocultar' : 'visualizar'} mapa e rota</p>
                </div>
              </div>
              {showMap ? <ChevronUp className="text-emerald-500" /> : <ChevronDown className="text-slate-400" />}
            </button>
            {showMap && (
              <div className="p-6 pt-2 space-y-6 animate-in slide-in-from-top-4 duration-500">
                {metricasRota && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-50 dark:bg-slate-950 border-2 border-emerald-500/20 rounded-3xl p-6 flex items-center justify-between shadow-inner">
                      <div>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Distância da Rota</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-black">{metricasRota.distancia}</span>
                          <span className="text-lg font-bold text-emerald-600">KM</span>
                        </div>
                      </div>
                      <div className="bg-emerald-500/10 p-4 rounded-2xl border border-emerald-500/10">
                        <Navigation size={32} className="text-emerald-600" />
                      </div>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-950 border-2 border-blue-500/20 rounded-3xl p-6 flex items-center justify-between shadow-inner">
                      <div>
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">Tempo Estimado</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-black">{metricasRota.tempo}</span>
                          <span className="text-lg font-bold text-blue-600">MIN</span>
                        </div>
                      </div>
                      <div className="bg-blue-500/10 p-4 rounded-2xl border border-blue-500/10">
                        <Clock size={32} className="text-blue-600" />
                      </div>
                    </div>
                  </div>
                )}
                <div className="rounded-[24px] overflow-hidden border-2 border-slate-200 dark:border-slate-800 h-[450px] relative z-10 shadow-lg">
                  <MapaEscola 
                    key={escolaSel.dados.cie}
                    lat={escolaSel.dados.lat}
                    lng={escolaSel.dados.lng}
                    nome={escolaSel.dados.nome_escola}
                    tema={tema}
                    setMetricasRota={setMetricasRota}
                    escolaId={escolaSel.dados.cie}
                  />
                </div>
              </div>
            )}
          </div>

         {/* ACESSOS RÁPIDOS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(() => {
              const cieF = String(escolaSel.dados.cie).padStart(6, '0');
              const cieSemZero = String(escolaSel.dados.cie); // CIE puro sem padStart de zeros para a senha aluno
              return (
                <>
                  <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-sm">
                    <h3 className="text-emerald-600 dark:text-emerald-400 font-black mb-4 text-[10px] tracking-widest flex items-center gap-3">
                      <Lock size={16}/> Acessos Unidade
                    </h3>
                    <div className="space-y-3">
                      
                      {/* Email Administrativo */}
                      <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-950 p-4 border border-slate-100 dark:border-slate-800 rounded-2xl">
                        <div className="truncate mr-2">
                          <p className="text-[9px] text-slate-400 dark:text-slate-700 font-black mb-1 tracking-widest uppercase">Email Administrativo</p>
                          <p className="font-mono text-xs text-slate-900 dark:text-white truncate">e{cieF}a@prof.educacao.sp.gov.br</p>
                        </div>
                        <button onClick={() => copy(`e${cieF}a@prof.educacao.sp.gov.br`, 'Email Admin')} className="p-2 bg-white dark:bg-slate-800 border dark:border-transparent rounded-xl hover:bg-emerald-600 hover:text-white transition-all text-slate-400 shadow-sm">
                          <Clipboard size={14}/>
                        </button>
                      </div>

                      {/* Senha Aluno (Agora separado embaixo e com design igual ao NSA) */}
                      <div 
                        className="bg-emerald-500/5 p-4 border border-emerald-500/20 rounded-2xl cursor-pointer hover:bg-emerald-500/10 transition-colors group"
                        onClick={() => copy(`decltnit${cieSemZero}`, 'Senha Aluno')}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-[9px] text-emerald-600 font-black mb-1 tracking-widest uppercase">Senha Aluno</p>
                            <p className="font-mono text-lg font-black text-emerald-500 tracking-tight">decltnit{cieSemZero}</p>
                          </div>
                          <Clipboard size={18} className="text-emerald-300 group-hover:text-emerald-500 transition-colors" />
                        </div>
                      </div>
                  
                      {/* Email Pedagógico */}
                      <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-950 p-4 border border-slate-100 dark:border-slate-800 rounded-2xl">
                        <div className="truncate mr-2">
                          <p className="text-[9px] text-slate-400 dark:text-slate-700 font-black mb-1 tracking-widest uppercase">Email Pedagógico</p>
                          <p className="font-mono text-xs text-slate-900 dark:text-white truncate">e{cieF}w10@professor.educacao.sp.gov.br</p>
                        </div>
                        <button onClick={() => copy(`e{cieF}w10@professor.educacao.sp.gov.br`, 'Email Pedagógico')} className="p-2 bg-white dark:bg-slate-800 border dark:border-transparent rounded-xl hover:bg-emerald-600 hover:text-white transition-all text-slate-400 shadow-sm">
                          <Clipboard size={14}/>
                        </button>
                      </div>
                  
                      {/* SENHA PADRÃO NSA */}
                      <div 
                        className="bg-emerald-500/5 p-4 border border-emerald-500/20 rounded-2xl cursor-pointer hover:bg-emerald-500/10 transition-colors group"
                        onClick={() => copy(`nsa${cieF}w10@`, 'Senha NSA')}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-[9px] text-emerald-600 font-black mb-1 tracking-widest uppercase">Senha Padrão NSA</p>
                            <p className="font-mono text-lg font-black text-emerald-500 tracking-tight">nsa{cieF}w10@</p>
                          </div>
                          <Clipboard size={18} className="text-emerald-300 group-hover:text-emerald-500 transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
              
                  <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-sm">
                    <h3 className="text-blue-600 dark:text-blue-400 font-black mb-4 text-[10px] tracking-widest flex items-center gap-3">
                      <Settings size={16}/> Suporte Local
                    </h3>
                    <div className="space-y-3">
                        
                      {/* Senha Tablet */}
                      <div className="bg-slate-50 dark:bg-slate-950 p-4 border border-slate-100 dark:border-slate-800 rounded-2xl flex justify-between items-center cursor-pointer hover:bg-white dark:hover:bg-slate-900 group transition-all" onClick={() => copy('4920', 'Senha Tablet')}>
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-700 tracking-widest uppercase">Senha Tablet</span>
                        <span className="font-mono font-black text-slate-900 dark:text-white text-xl">4920</span>
                      </div>
                  
                      {/* Senha Proatec */}
                      <div className="bg-slate-50 dark:bg-slate-950 p-4 border border-slate-100 dark:border-slate-800 rounded-2xl flex justify-between items-center cursor-pointer hover:bg-white dark:hover:bg-slate-900 group transition-all" onClick={() => copy('$educ_Pr0@t&c', 'Senha Proatec')}>
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-700 tracking-widest uppercase">Senha Proatec</span>
                        <span className="font-mono text-xs text-blue-600 dark:text-blue-400 font-black">$educ_Pr0@t&c</span>
                      </div>
                  
                      {/* LINK PARA INVENTÁRIO SEDUC */}
                      <a 
                        href="https://ti-inventario-ure-camp-lest.manus.space/login" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-blue-600/10 p-4 border border-blue-600/20 rounded-2xl flex justify-between items-center hover:bg-blue-600 hover:text-white transition-all group no-underline"
                      >
                        <div className="flex flex-col">
                          <span className="text-[10px] font-black tracking-widest uppercase opacity-70">Acesso Oficial</span>
                          <span className="text-xs font-black uppercase">Inventário SEDUC</span>
                        </div>
                        <div className="bg-blue-600 text-white p-2 rounded-xl group-hover:bg-white group-hover:text-blue-600 transition-colors">
                          <Globe size={18} />
                        </div>
                      </a>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* MODAL NOVO ATIVO */}
          {modalAberto && (
            <div className="fixed inset-0 bg-slate-900/40 dark:bg-black/90 z-[500] flex items-end lg:items-center justify-center p-0 lg:p-4 backdrop-blur-sm animate-in fade-in duration-300">
              <div className="bg-white dark:bg-slate-900 border-t lg:border-2 border-slate-200 dark:border-slate-800 w-full max-w-lg rounded-t-[32px] lg:rounded-[32px] shadow-2xl p-6 lg:p-8 relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-10">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg lg:text-xl font-black tracking-tighter flex items-center gap-2"><Plus className="text-emerald-600" size={24}/> Novo Registro</h2>
                  <button onClick={() => setModalAberto(false)} className="text-slate-400 hover:text-slate-900 p-2 bg-slate-100 dark:bg-slate-800 rounded-full transition-colors"><X size={20}/></button>
                </div>
                <form onSubmit={salvarEquipamento} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] text-slate-400 dark:text-slate-500 font-black ml-1 uppercase">Tipo</label>
                      <select className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3 rounded-xl text-xs font-bold focus:border-emerald-500 outline-none" value={formData.tipo} onChange={e => setFormData({...formData, tipo: e.target.value})}>
                        <option>Desktop</option><option>Tablet</option><option>Notebook</option><option>ChromeBook</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] text-slate-400 dark:text-slate-500 font-black ml-1 uppercase tracking-widest">TAG / Patrimônio</label>
                      <input type="text" placeholder="123456" className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3 rounded-xl text-sm font-black text-center text-emerald-600 focus:border-emerald-500 outline-none uppercase" value={formData.patrimonio} onChange={e => setFormData({...formData, patrimonio: e.target.value})}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] text-slate-400 dark:text-slate-500 font-black ml-1 uppercase tracking-widest">MAC Address</label>
                      <input type="text" placeholder="00:00:00:00:00:00" className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3 rounded-xl text-xs font-mono text-center focus:border-emerald-500 outline-none uppercase" value={formData.mac_address} onChange={e => setFormData({...formData, mac_address: e.target.value})}/>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] text-slate-400 dark:text-slate-500 font-black ml-1 uppercase tracking-widest">Status Inicial</label>
                      <select className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3 rounded-xl text-xs font-bold focus:border-emerald-500 outline-none" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value, em_uso: e.target.value === 'Operacional' ? 1 : 0})}>
                        <option>Operacional</option><option>Manutenção</option><option>Sucata</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] text-slate-400 dark:text-slate-500 font-black ml-1 uppercase tracking-widest">Modelo / Descrição</label>
                    <input type="text" placeholder="Ex: Dell Optiplex 3020" className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3 rounded-xl text-xs focus:border-emerald-500 outline-none" value={formData.descricao} onChange={e => setFormData({...formData, descricao: e.target.value})}/>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] text-slate-400 dark:text-slate-500 font-black ml-1 uppercase tracking-widest">Observações Adicionais</label>
                    <textarea rows="2" placeholder="Algum detalhe extra?" className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3 rounded-xl text-xs focus:border-emerald-500 outline-none resize-none" value={formData.obs} onChange={e => setFormData({...formData, obs: e.target.value})}/>
                  </div>
                  <button type="submit" className="w-full bg-emerald-600 py-4 rounded-2xl font-black text-[10px] tracking-widest text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-500 active:scale-95 transition-all mt-2 uppercase">Registrar Ativo</button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;